import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import PdfPagesView from "@/components/PdfPagesView";
import { Loader2, Download, Printer } from "lucide-react";
import { buildStatementPdf } from "@/lib/statementPdf";
import { buildSupplierStatementPdf } from "@/lib/supplierStatementPdf";
import { printPdf } from "@/lib/quotePdf";
import { cariShort, docFileName } from "@/lib/format";
import { toast } from "sonner";



// Cari / Tedarikçi Ekstresi önizleme — ekrandaki önizleme indirilen/yazdırılan PDF'in kendisidir
export default function StatementPreviewDialog({ stmt, onClose, contact, data }) {
  const [showItems, setShowItems] = useState(true);
  const [pdfData, setPdfData] = useState(null);
  const pdfRef = useRef(null);
  const supplier = contact?.type === "tedarikci";
  useEffect(() => {
    setPdfData(null); pdfRef.current = null;
    if (!stmt) return undefined;
    let off = false;
    const job = supplier
      ? buildSupplierStatementPdf({ contact, data, range: { start: stmt.start || "", end: stmt.end || "" } })
      : buildStatementPdf({ stmt, showItems });
    job.then((pdf) => { if (!off) { pdfRef.current = pdf; setPdfData(pdf.output("arraybuffer")); } })
      .catch((e) => { console.error(e); if (!off) toast.error("Ekstre önizlemesi oluşturulamadı"); });
    return () => { off = true; };
  }, [stmt, showItems, supplier, contact, data]);

  const name = docFileName("", `${supplier ? "Tedarikçi Ekstresi" : "Cari Ekstre"} ${cariShort(supplier ? contact?.title : stmt?.contact?.title)}`);
  const download = () => { if (!pdfRef.current) return; pdfRef.current.save(name); toast.success(supplier ? "Tedarikçi ekstresi PDF indirildi" : "Ekstre PDF indirildi"); };
  const print = () => pdfRef.current && printPdf(pdfRef.current, name.replace(/\.pdf$/, ""), { asFileName: true });
  const width = Math.min(794, (typeof window !== "undefined" ? window.innerWidth : 900) - 64);
  return (
    <Dialog open={!!stmt} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl w-[96vw] max-h-[94vh] overflow-y-auto p-0" data-testid="statement-preview">
        <div className="sticky top-0 z-10 bg-card border-b p-4 pr-14 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="mr-auto">
              <DialogTitle className="font-head text-xl">{supplier ? "Tedarikçi Ekstresi" : "Cari Ekstre"}</DialogTitle>
              <p className="text-[11px] text-muted-foreground">{supplier ? "Alınan ürünler · yapılan ödemeler · aylık özet" : "Faturalar, cari ürün girişleri ve tahsilatlar — çalışan bakiye ile"}</p>
            </div>
            <Button size="sm" onClick={download} disabled={!pdfData} data-testid="statement-download-btn"><Download className="h-4 w-4 mr-1.5" />PDF İndir</Button>
            <Button size="sm" variant="outline" onClick={print} disabled={!pdfData} data-testid="statement-print-btn"><Printer className="h-4 w-4 mr-1.5" />Yazdır</Button>
            <Button size="sm" variant="outline" onClick={onClose} data-testid="statement-close-btn">Kapat</Button>
          </div>
          {!supplier && (
            <div className="flex items-center gap-2 px-1">
              <Switch checked={showItems} onCheckedChange={setShowItems} data-testid="stmt-items-toggle" />
              <Label className="text-xs text-muted-foreground cursor-pointer" onClick={() => setShowItems(!showItems)}>Kalemleri göster (fatura / giriş satırlarının altında ürün dökümü)</Label>
            </div>
          )}
        </div>
        <div className="p-4 bg-slate-200/70 overflow-x-auto" data-testid="statement-preview-pages">
          {pdfData ? <PdfPagesView data={pdfData} width={width} testid="statement-pdf-pages" /> : (
            <div className="py-24 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Ekstre hazırlanıyor…</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
